/**
 * What Pippi notes with every message (Omni): the page (readable name + path, credentials stripped), the campaign when
 * the page is one, and the browser. Pure functions, so the scrubbing is testable; cowork scrubs again on arrival.
 */
import type { DeviceInfo, PageInfo } from './pippiApi';
import { parseAppPath } from '../../routes';
import { entryFor } from '../guide/pageMap';

/** Query parameters that carry credentials: never leave the browser in anything Pippi sends or records. */
const SECRET_PARAMS = /^(jwt|token|code|access_token|id_token|refresh_token|auth|authorization|password|secret|key|api_key|apikey|session|sig|signature)$/i;

/** A URL or path with jwt / token / code (and friends) removed. Relative paths stay relative. */
export function scrubUrl(raw: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  try {
    const u = new URL(s, 'https://omni.invalid');
    for (const k of Array.from(u.searchParams.keys())) if (SECRET_PARAMS.test(k)) u.searchParams.delete(k);
    const q = u.searchParams.toString();
    const path = `${u.pathname}${q ? `?${q}` : ''}`;
    return u.origin === 'https://omni.invalid' ? path : `${u.origin}${path}`;
  } catch {
    return s.replace(/([?&])(jwt|token|code|access_token|id_token)=[^&#]*/gi, '$1').replace(/[?&]+$/, '');
  }
}

/** Free text (error messages) can quote URLs or bearer tokens too. */
export function scrubText(s: string): string {
  return String(s ?? '')
    .replace(/([?&])(jwt|token|code|access_token|id_token|refresh_token)=[^&\s#"']*/gi, '$1$2=…')
    .replace(/Bearer\s+[A-Za-z0-9._-]{10,}/g, 'Bearer …')
    .replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{5,}/g, '[token]');
}

const words = (slug: string) => slug.replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const shortName = (name: string) => (name.length > 40 ? `${name.slice(0, 39)}…` : name);

/**
 * Readable page for an Omni location, named the way Lykke's map names it (cowork/guide/pageMap.ts):
 * "Content approval · Lensway UGC NO" on a campaign tab (the campaign's name when known), "Content Hub", "All campaigns".
 */
export function pageInfo(pathname: string, search = '', campaignName?: string | null): PageInfo {
  const path = scrubUrl(`${pathname}${search}`);
  let label = '';
  let campaignId: string | undefined;
  try {
    const route = parseAppPath(pathname);
    campaignId = route.campaignId && /^[A-Za-z0-9_-]{3,100}$/.test(route.campaignId) ? route.campaignId : undefined;
    label = entryFor(pathname, route.currentPage, route.subTab)?.label || '';
    if (!label) label = words(route.currentPage || '');
  } catch { /* unknown path: fall back to its first segment */ }
  if (!label) label = words(pathname.split('/').filter(Boolean)[0] || 'Home');
  if (campaignId) {
    const name = campaignName?.trim();
    return { path, label: name ? `${label} · ${shortName(name)}` : label, campaignId, ...(name ? { campaignName: name } : {}) };
  }
  return { path, label };
}

/** The back office (where the support book is): VITE_BO_APP_URL, else bo2.[staging.]beatly.com by this app's own host. */
export function boAppUrl(env: { VITE_BO_APP_URL?: string; VITE_APP_URL?: string } = import.meta.env as never): string {
  const set = (env.VITE_BO_APP_URL || '').trim();
  if (set) return set.replace(/\/$/, '');
  return /staging/.test(env.VITE_APP_URL || (typeof location !== 'undefined' ? location.host : '')) ? 'https://bo2.staging.beatly.com' : 'https://bo2.beatly.com';
}

/** "Chrome 141", "macOS", "1512×945", language, time zone — from the browser, nothing else. */
export function deviceInfo(nav: { userAgent: string; language?: string } = navigator, win: { innerWidth: number; innerHeight: number } = window): DeviceInfo {
  const ua = nav.userAgent || '';
  const pick = (re: RegExp) => ua.match(re)?.[1];
  const browser =
    (pick(/Edg\/(\d+)/) && `Edge ${pick(/Edg\/(\d+)/)}`) ||
    (pick(/OPR\/(\d+)/) && `Opera ${pick(/OPR\/(\d+)/)}`) ||
    (pick(/Firefox\/(\d+)/) && `Firefox ${pick(/Firefox\/(\d+)/)}`) ||
    (pick(/Chrome\/(\d+)/) && `Chrome ${pick(/Chrome\/(\d+)/)}`) ||
    (/Safari\//.test(ua) && pick(/Version\/(\d+)/) && `Safari ${pick(/Version\/(\d+)/)}`) || 'Browser';
  const os = /Mac OS X|Macintosh/.test(ua) ? (/iPhone|iPad/.test(ua) ? 'iOS' : 'macOS') : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : /iPhone|iPad/.test(ua) ? 'iOS' : 'Other';
  let timezone: string | undefined;
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* old browser */ }
  return { browser, os, viewport: `${win.innerWidth}×${win.innerHeight}`, language: nav.language, timezone };
}

export const deviceLabel = (d: DeviceInfo) => [d.browser, d.os].filter(Boolean).join(' · ');
